// Global Instructions Rule Applied!
//
// Intercompany Transfers section (fund groups, v3 model - owner rulings
// 2026-09-28). Rendered under the calls grid on the group's Capital Calls
// tab.
//
// A transfer moves capital from the primary fund to a satellite and is
// BUILT LIKE A CAPITAL CALL: New Transfer asks only for the satellite, the
// date and the source, then creates a Draft transfer + its Draft derived
// satellite call and opens that call in the standard Capital Call form -
// gross amount or % of commitment, line items, fee periods priced by the
// satellite's fee engine (the SBIC fee solver prices the fees on the
// capital the transfer itself pays in), whichever line is the balance, and
// Book transfer. This section lists Draft and Booked transfers, reverts a
// booked one to Draft and deletes a Draft.
//
// Source: a transfer draws on the POOL of every issued consolidated call
// (the pool table above the grid) and splits by satellite pocket share. An
// equalization call can still be chosen as the source: its transfer
// re-weighs the split by what each late closer was called on it.

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Button, Form, Input, Modal, Select, Space, Spin, Table, Tag, Typography } from 'antd'
import DatePicker from '../../../core/components/DatePicker.tsx'
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import type { RootState } from '../../../state_manager/store.ts'
import { store } from '../../../state_manager/store.ts'
import { FormSection } from '../../../core/components/FormSection.tsx'
import NumberText from '../../../core/components/NumberText.tsx'
import { getAccessToken } from '../../../core/auth/authService'
import { useTabManagerContext } from '../../../core/app-layout/lib/TabManagerContext.tsx'
import { useRefreshOnActivate } from '../../../core/hooks/useRefreshOnActivate.ts'
import { TABLE_CAPITAL_CALLS } from '../../../state_manager/globals.ts'
import {
  FundGroupTransferDefaults,
  FundGroupTransferDetail,
  FundGroupTransferLine,
  FundGroupTransferPool,
  FundGroupTransferRow
} from '../../../state_manager/data_models/fund_group.ts'
import {
  createFundGroupTransferDraft,
  deleteFundGroupTransfer,
  fetchFundGroupTransferDefaults,
  fetchFundGroupTransferDetail,
  fetchFundGroupTransferPool,
  fetchFundGroupTransfers,
  revertFundGroupTransfer
} from '../services/fundGroupsService.ts'
import ExportRowsButton from '../../shared/table_grid_export/ExportRowsButton.tsx'

const { Text } = Typography

// the Source select's value for "the pool of every issued call"
const POOLED = '__pooled'

interface Props {
  fundGroupId: string
}

interface PoolRow {
  key: string
  label: string
  investment: number
  expenses: number
  fees: number
  total: number
  strong?: boolean
}

interface NewTransferValues {
  fund_id: string
  transfer_date: dayjs.Dayjs | null
  source: string
  notes?: string
}

export function FundGroupTransfersSection({ fundGroupId }: Props): React.ReactElement {
  const { openFormInTab } = useTabManagerContext()
  const groupName = useSelector(
    (s: RootState) => (s.globalConfigs?.fund_groups ?? []).find((g) => g.id === fundGroupId)?.name ?? 'Consolidated fund'
  )
  const [rows, setRows] = useState<FundGroupTransferRow[]>([])
  const [pool, setPool] = useState<FundGroupTransferPool | null>(null)
  const [loading, setLoading] = useState(false)
  const [acting, setActing] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [defaults, setDefaults] = useState<FundGroupTransferDefaults | null>(null)
  const [details, setDetails] = useState<Record<string, FundGroupTransferDetail | null>>({})
  const [form] = Form.useForm<NewTransferValues>()
  // static Modal.confirm does not render under React 19 - hook form required
  const [modalApi, modalContextHolder] = Modal.useModal()

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const token = getAccessToken(store.getState().auth)
      const [res, poolRes] = await Promise.all([
        fetchFundGroupTransfers(fundGroupId, token),
        fetchFundGroupTransferPool(fundGroupId, token)
      ])
      if (res.error) {
        showErrorToast(res.error)
        return
      }
      setRows(res.data)
      setDetails({})
      if (poolRes.error) showErrorToast(poolRes.error)
      setPool(poolRes.data)
    } finally {
      setLoading(false)
    }
  }, [fundGroupId])

  useEffect(() => {
    void load()
  }, [load])

  // a Draft is edited (and booked) in the Capital Call form tab: refresh
  // when attention returns here
  const activateRef = useRefreshOnActivate(load)

  const openCall = useCallback(
    (row: FundGroupTransferRow) => {
      if (!row.capital_calls__id) return
      openFormInTab(
        TABLE_CAPITAL_CALLS,
        row.capital_calls__id,
        `Transfer to ${row.funds__name ?? 'satellite'} — ${row.fund_group_transfers__transfer_date_fmt ?? ''}`
      )
    },
    [openFormInTab]
  )

  const openDialog = useCallback(async () => {
    form.resetFields()
    form.setFieldsValue({ transfer_date: dayjs(), source: POOLED })
    setDialogOpen(true)
    const token = getAccessToken(store.getState().auth)
    const res = await fetchFundGroupTransferDefaults(fundGroupId, null, null, token)
    if (res.error || !res.data) {
      showErrorToast(res.error || 'Failed to load the satellite funds')
      return
    }
    setDefaults(res.data)
    form.setFieldsValue({ fund_id: res.data.fund_id ?? undefined })
  }, [form, fundGroupId])

  const submit = useCallback(async () => {
    let values: NewTransferValues
    try {
      values = await form.validateFields()
    } catch {
      return
    }
    setActing(true)
    try {
      const res = await createFundGroupTransferDraft(
        {
          fund_group_id: fundGroupId,
          fund_id: values.fund_id,
          transfer_date: (values.transfer_date ?? dayjs()).format('YYYY-MM-DD'),
          fund_group_call_id: values.source === POOLED ? null : values.source,
          notes: values.notes || null
        },
        getAccessToken(store.getState().auth)
      )
      if (!res.success || !res.data) {
        showErrorToast(res.error || 'Failed to create the transfer')
        return
      }
      toast.success(res.data.status_message || 'Draft transfer created')
      setDialogOpen(false)
      const fundName = defaults?.satellites.find((s) => s.funds__id === values.fund_id)?.funds__name
      openFormInTab(
        TABLE_CAPITAL_CALLS,
        res.data.capital_call_id,
        `Transfer to ${fundName ?? 'satellite'} — ${(values.transfer_date ?? dayjs()).format('MM/DD/YYYY')}`
      )
      await load()
    } finally {
      setActing(false)
    }
  }, [defaults, form, fundGroupId, load, openFormInTab])

  const handleRevert = useCallback(
    (row: FundGroupTransferRow) => {
      modalApi.confirm({
        title: 'Revert transfer to Draft?',
        content:
          'This unbooks and removes the intercompany pair and the satellite contribution records, ' +
          'and returns the transfer and its satellite call to an editable Draft.',
        okText: 'Revert',
        okButtonProps: { danger: true },
        onOk: async () => {
          setActing(true)
          try {
            const res = await revertFundGroupTransfer(
              row.fund_group_transfers__id,
              getAccessToken(store.getState().auth)
            )
            if (!res.success) {
              showErrorToast(res.error || 'Failed to revert transfer')
              return
            }
            toast.success('Transfer reverted to Draft')
            await load()
          } finally {
            setActing(false)
          }
        }
      })
    },
    [load, modalApi]
  )

  const handleDelete = useCallback(
    (row: FundGroupTransferRow) => {
      modalApi.confirm({
        title: 'Delete draft transfer?',
        content: 'The draft transfer and its satellite call are moved to trash.',
        okText: 'Delete',
        okButtonProps: { danger: true },
        onOk: async () => {
          setActing(true)
          try {
            const res = await deleteFundGroupTransfer(
              row.fund_group_transfers__id,
              getAccessToken(store.getState().auth)
            )
            if (!res.success) {
              showErrorToast(res.error || 'Failed to delete transfer')
              return
            }
            toast.success('Draft transfer deleted')
            await load()
          } finally {
            setActing(false)
          }
        }
      })
    },
    [load, modalApi]
  )

  const loadDetail = useCallback(async (transferId: string) => {
    const res = await fetchFundGroupTransferDetail(transferId, getAccessToken(store.getState().auth))
    if (res.error || !res.data) {
      showErrorToast(res.error || 'Failed to load transfer detail')
      return
    }
    setDetails((prev) => ({ ...prev, [transferId]: res.data }))
  }, [])

  // ----- the pool: rows = called / transferred per satellite / retained -----
  const poolRows = useMemo((): PoolRow[] => {
    if (!pool) return []
    const bucket = (key: string) => pool.buckets.find((b) => b.key === key)
    const row = (
      key: string,
      label: string,
      pick: (b: NonNullable<ReturnType<typeof bucket>>) => number,
      strong = false
    ): PoolRow => {
      const inv = bucket('investment')
      const exp = bucket('expenses')
      const fee = bucket('fees')
      const investment = inv ? pick(inv) : 0
      const expenses = exp ? pick(exp) : 0
      const fees = fee ? pick(fee) : 0
      // cents: a float sum would read as a not-exact figure in NumberText
      const total = Math.round((investment + expenses + fees) * 100) / 100
      return { key, label, investment, expenses, fees, total, strong }
    }
    const out: PoolRow[] = [row('called', 'Called (all issued calls)', (b) => b.prog__called)]
    for (const s of pool.satellites) {
      out.push(row(`sat-${s.funds__id}`, `Transferred to ${s.funds__name ?? 'satellite'}`, (b) => b.per_satellite[s.funds__id] ?? 0))
    }
    out.push(row('retained', 'Retained in primary', (b) => b.prog__retained, true))
    if ((pool.totals?.in_draft ?? 0) !== 0) {
      out.push(row('draft', 'In Draft transfers (not yet booked)', (b) => b.prog__in_draft))
    }
    return out
  }, [pool])

  const figure = (v: number | null | undefined, strong = false) => (
    <NumberText value={v ?? 0} wrap={strong ? (f) => <Text strong>{f}</Text> : undefined} />
  )

  const poolColumns = [
    {
      title: '',
      dataIndex: 'label',
      key: 'label',
      render: (v: string, r: PoolRow) => (r.strong ? <Text strong>{v}</Text> : v)
    },
    ...(['investment', 'expenses', 'fees', 'total'] as const).map((k) => ({
      title: { investment: 'Investments', expenses: 'Expenses', fees: 'Mgmt fees', total: 'Total' }[k],
      dataIndex: k,
      key: k,
      align: 'right' as const,
      width: 150,
      render: (v: number, r: PoolRow) => figure(v, r.strong || k === 'total')
    }))
  ]

  const columns = useMemo(
    () => [
      {
        title: 'Date',
        dataIndex: 'fund_group_transfers__transfer_date_fmt',
        key: 'date',
        width: 110
      },
      {
        title: 'To fund',
        dataIndex: 'funds__name',
        key: 'fund',
        render: (v: string | null, r: FundGroupTransferRow) =>
          r.capital_calls__id ? (
            <button
              type='button'
              className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left'
              onClick={() => openCall(r)}
            >
              {v ?? '—'}
            </button>
          ) : (
            v ?? '—'
          )
      },
      {
        title: 'Source',
        dataIndex: 'fund_group_transfers__source_label',
        key: 'source',
        render: (v: string | null) => v ?? '—'
      },
      {
        title: 'Fee period',
        dataIndex: 'fund_group_transfers__fee_period_fmt',
        key: 'feePeriod',
        render: (v: string | null) => v ?? <Text type='secondary'>—</Text>
      },
      {
        title: 'Investment',
        dataIndex: 'prog__investment_amount',
        key: 'investment',
        align: 'right' as const,
        render: (v: number | null) => figure(v)
      },
      {
        title: 'Expenses',
        dataIndex: 'prog__expenses_amount',
        key: 'expenses',
        align: 'right' as const,
        render: (v: number | null) => figure(v)
      },
      {
        title: 'Fee funding',
        dataIndex: 'prog__fees_amount',
        key: 'fees',
        align: 'right' as const,
        render: (v: number | null) => figure(v)
      },
      {
        title: 'Total',
        dataIndex: 'prog__total_amount',
        key: 'total',
        align: 'right' as const,
        render: (v: number | null) => figure(v, true)
      },
      {
        title: 'Status',
        dataIndex: 'fund_group_transfers__status',
        key: 'status',
        render: (v: string | null, r: FundGroupTransferRow) => (
          <Tag color={r.fund_group_transfers__is_booked ? 'green' : 'blue'}>{v ?? '—'}</Tag>
        )
      },
      {
        title: '',
        key: 'actions',
        width: 150,
        render: (_: unknown, r: FundGroupTransferRow) => (
          <Space size={4}>
            <Button size='small' disabled={!r.capital_calls__id} onClick={() => openCall(r)}>
              Open
            </Button>
            {r.fund_group_transfers__is_booked ? (
              <Button size='small' danger disabled={acting} onClick={() => handleRevert(r)}>
                Revert
              </Button>
            ) : (
              <Button size='small' danger disabled={acting} onClick={() => handleDelete(r)}>
                Delete
              </Button>
            )}
          </Space>
        )
      }
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [acting, handleDelete, handleRevert, openCall]
  )

  const lineColumns = useMemo(
    () => [
      { title: 'Investor', dataIndex: 'investors__name', key: 'investor' },
      {
        title: 'Investment',
        dataIndex: 'prog__investment_amount',
        key: 'investment',
        align: 'right' as const,
        render: (v: number | null) => figure(v)
      },
      {
        title: 'Expenses',
        dataIndex: 'prog__expenses_amount',
        key: 'expenses',
        align: 'right' as const,
        render: (v: number | null) => figure(v)
      },
      {
        title: 'Fee funding',
        dataIndex: 'prog__fees_amount',
        key: 'fees',
        align: 'right' as const,
        render: (v: number | null) => figure(v)
      },
      {
        title: 'Total',
        dataIndex: 'prog__total_amount',
        key: 'total',
        align: 'right' as const,
        render: (v: number | null) => figure(v, true)
      }
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const sourceOptions = useMemo(
    () => [
      { value: POOLED, label: 'All issued calls (pooled)' },
      ...(defaults?.source_calls ?? []).map((c) => ({
        value: c.fund_group_calls__id,
        label: `${c.fund_group_calls__name ?? c.fund_group_calls__id} (equalization) — ${c.fund_group_calls__call_date_fmt ?? '—'}`
      }))
    ],
    [defaults]
  )

  return (
    <div ref={activateRef}>
      <FormSection
        title='Intercompany Transfers'
        titleColor='indigo'
        loading={loading}
        headerActions={
          <Space>
            {/* the transfers list only: not the pool table above it */}
            <ExportRowsButton
              name={`${groupName} Intercompany transfers`}
              rows={rows}
              columns={[
                { title: 'Date', value: (r) => r.fund_group_transfers__transfer_date, kind: 'date' },
                { title: 'To fund', value: (r) => r.funds__name },
                { title: 'Source', value: (r) => r.fund_group_transfers__source_label },
                { title: 'Fee period from', value: (r) => r.fund_group_transfers__fee_period_from, kind: 'date' },
                { title: 'Fee period to', value: (r) => r.fund_group_transfers__fee_period_to, kind: 'date' },
                { title: 'Investment', value: (r) => r.prog__investment_amount },
                { title: 'Expenses', value: (r) => r.prog__expenses_amount },
                { title: 'Fee funding', value: (r) => r.prog__fees_amount },
                { title: 'Total', value: (r) => r.prog__total_amount },
                { title: 'Status', value: (r) => r.fund_group_transfers__status }
              ]}
            />
            <Button type='primary' size='small' icon={<PlusOutlined />} onClick={() => void openDialog()}>
              New Transfer
            </Button>
            <Button icon={<ReloadOutlined />} size='small' onClick={() => void load()} disabled={loading}>
              Refresh
            </Button>
          </Space>
        }
      >
        {modalContextHolder}
        {loading && rows.length === 0 ? (
          <div className='flex justify-center p-6'>
            <Spin />
          </div>
        ) : (
          <div className='flex flex-col gap-3'>
            <div>
              <Text strong className='block mb-1'>
                Transfer pool — all issued calls
              </Text>
              <Table
                size='small'
                pagination={false}
                rowKey='key'
                dataSource={poolRows}
                columns={poolColumns}
                scroll={{ x: 'max-content' }}
                locale={{ emptyText: 'No issued consolidated calls yet' }}
              />
            </div>
            <Table
              size='small'
              pagination={false}
              rowKey={(r: FundGroupTransferRow) => r.fund_group_transfers__id}
              dataSource={rows}
              columns={columns}
              scroll={{ x: 'max-content' }}
              expandable={{
                expandedRowRender: (r: FundGroupTransferRow) => {
                  const detail = details[r.fund_group_transfers__id]
                  if (detail === undefined) {
                    return (
                      <div className='flex justify-center p-4'>
                        <Spin size='small' />
                      </div>
                    )
                  }
                  if (!detail) return <Text type='secondary'>Failed to load detail.</Text>
                  return (
                    <Table
                      size='small'
                      pagination={false}
                      rowKey={(l: FundGroupTransferLine) => l.fund_group_commitments__id}
                      dataSource={detail.lines}
                      columns={lineColumns}
                      scroll={{ x: 'max-content' }}
                      locale={{ emptyText: 'Compute the transfer in its call form to see the per-investor split' }}
                    />
                  )
                },
                onExpand: (expanded, r: FundGroupTransferRow) => {
                  if (expanded && details[r.fund_group_transfers__id] === undefined) {
                    void loadDetail(r.fund_group_transfers__id)
                  }
                }
              }}
              locale={{ emptyText: 'No transfers yet — capital moves when you create and book one' }}
            />
          </div>
        )}

        <Modal
          title='New Intercompany Transfer'
          open={dialogOpen}
          onOk={() => void submit()}
          onCancel={() => setDialogOpen(false)}
          okText='Create'
          confirmLoading={acting}
          destroyOnHidden
        >
          <Form form={form} layout='vertical' preserve={false}>
            <div className='grid grid-cols-2 gap-x-3'>
              <Form.Item
                name='fund_id'
                label='To fund'
                rules={[{ required: true, message: 'Satellite fund is required' }]}
              >
                <Select
                  options={(defaults?.satellites ?? []).map((s) => ({
                    value: s.funds__id,
                    label: s.funds__name ?? s.funds__id
                  }))}
                />
              </Form.Item>
              <Form.Item
                name='transfer_date'
                label='Transfer Date'
                rules={[{ required: true, message: 'Transfer date is required' }]}
              >
                <DatePicker style={{ width: '100%' }} format='MM/DD/YYYY' />
              </Form.Item>
            </div>
            <Form.Item
              name='source'
              label='Source'
              extra={
                <Text type='secondary'>
                  A transfer draws on every issued call and splits by satellite share. Pick an
                  equalization call only to transfer a late closer's catch-up: its split follows
                  what each investor was called on it.
                </Text>
              }
            >
              <Select options={sourceOptions} />
            </Form.Item>
            <Form.Item name='notes' label='Notes'>
              <Input placeholder='Optional' />
            </Form.Item>
            <Text type='secondary'>
              Create opens the transfer in the Capital Call form: set the gross amount, the fee period and
              any other lines, mark the balance line, Compute, then Book transfer.
            </Text>
          </Form>
        </Modal>
      </FormSection>
    </div>
  )
}

export default FundGroupTransfersSection
